import config from "@Config/index";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import {
  FutureIntake,
  doesMoveInHideStrip,
  getActiveIntake,
  getTodayISO,
  toMoveInISO,
} from "@Pages/PropertyPage/utils/futureIntake";
import { AppState } from "@Store/index";
import { useSelector } from "react-redux";

// Returns the intake (Spring'27 / Fall'27) the room card's "Apply for" strip
// advertises, or null when the strip is hidden.
const useApplyForFallExperiment = (
  roomData: Record<any, any>,
  inventory: Record<any, any>,
): FutureIntake | null => {
  const isApplyForFallExperiment = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.APPLY_FOR_FALL_EXPERIMENT,
  );
  // CRM Future Intake page: which intakes are enabled, and for which countries.
  const futureIntakeConfig = useSelector(
    (state: AppState) => state.propertyPageData.future_intake_config,
  );

  const isChina = config.IS_CHINA_APP;
  const isPBSA = inventory?.meta?.unit_type === "student_accommodation";

  const isFlexiRoom =
    roomData?.available &&
    (roomData.active_children_with_filters_count === 0 ||
      roomData?.active_children_with_filters.length === 0 ||
      roomData.active_children?.length === 0 ||
      roomData?.active_children_count === 0);

  if (isFlexiRoom) return null;

  // Sold out: nothing to apply for on this room (requirement change 2026-10-26).
  if (roomData?.available === false) return null;

  const allTenanciesMoveIn =
    (roomData?.active_children || roomData?.active_children_with_filters)
      ?.map((item: any) => item?.meta?.available_from_formatted ?? "")
      .filter((val: string) => val !== "") || [];

  if (roomData?.available && allTenanciesMoveIn.length === 0) return null;

  // The intake's move-in window replaces the hard-coded 31 Jul 2026 threshold.
  // It is null once the window has ended, or when the CRM hasn't enabled it
  // for this property's country.
  const todayISO = getTodayISO();
  const intake = getActiveIntake({
    config: futureIntakeConfig,
    countryCode: inventory?.location?.country?.short_name,
    todayISO,
  });

  if (!intake) return null;

  // One tenancy in a "hide" cell of the move-in matrix hides the strip for the room.
  // meta.available_from ("24-10-2026") is unambiguous; the formatted label is the fallback.
  const isAnyMoveInHiding = (
    roomData?.active_children ||
    roomData?.active_children_with_filters ||
    []
  ).some((item: any) => {
    const moveIn =
      toMoveInISO(item?.meta?.available_from) || toMoveInISO(item?.meta?.available_from_formatted);
    return !!moveIn && doesMoveInHideStrip({ moveIn, intake, todayISO });
  });

  return (isApplyForFallExperiment || isChina) && isPBSA && !isAnyMoveInHiding ? intake : null;
};

export default useApplyForFallExperiment;
